import React, { useMemo, useState } from 'react';

import Popup from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import {
	fromWeeksToItemTable,
	mergeRefs,
	PeriodType,
	Rotation,
	SimpleShiftTile,
	SingleViewShiftCard,
	useShiftFocus,
	Week,
} from '@atlassian/eoc-schedule-common';

import { useRotation } from '../../../../../controllers/schedule-store/helpers';

import { type WeekListProps } from './types';

const WeekList = (props: WeekListProps) => {
	const { weeks, nowEpoch, width } = props;

	const [activePopup, setActivePopup] = useState<string>();

	const [activeRotation] = useRotation();

	const itemTable = useMemo(() => {
		return fromWeeksToItemTable(weeks);
	}, [weeks]);

	const { tabbed, focusRefs, onContainerKeyDown, tab } = useShiftFocus(itemTable, nowEpoch);

	return (
		// eslint-disable-next-line @atlaskit/design-system/prefer-primitives, jsx-a11y/no-static-element-interactions
		<div
			// NOTE: might be re add later
			// role="table"
			// aria-label={layer.label}
			// aria-roledescription={intlApi.formatMessage(
			//   scheduleCommonMessages.calendarLayer,
			// )}
			onKeyDown={onContainerKeyDown}
		>
			{weeks.map(({ startEpoch, endEpoch, startOffset, rotations }) => {
				const week = {
					startEpoch,
					endEpoch,
					nowEpoch,
					width,
				};

				return (
					<Week
						key={`week-${startEpoch}-${endEpoch}`}
						startEpoch={startEpoch}
						endEpoch={endEpoch}
						nowEpoch={nowEpoch}
						offset={startOffset}
						rotationSlot={rotations.map((rotation, index) => (
							<Rotation
								key={index}
								isFirstLastSame
								variant="compact"
								highlighted={
									(activeRotation && rotation.id === null) || activeRotation?.id === rotation.id
								}
								name={rotation.name}
								size="small"
							>
								{rotation.periods.map((period) => (
									<Popup
										key={period.id}
										content={() => (
											<SingleViewShiftCard
												onClose={() => setActivePopup(undefined)}
												cards={[{ ...period, rotation }]}
											/>
										)}
										isOpen={activePopup === period.id}
										// TODO: make common function
										// Made onClose ignore close if an shift tile in current layer is click
										// Because for some reason onClose fires after new popup open causing
										// it to close as well
										onClose={(e) => {
											if (e?.target instanceof HTMLElement) {
												const target = e.target.closest('[data-shift-id]');

												if (
													target &&
													Object.values(focusRefs).some((ref) => ref.current === target)
												) {
													return;
												}
											}

											setActivePopup(undefined);
										}}
										placement="bottom"
										zIndex={layers.modal() + 1}
										trigger={({ ref: triggerRef }) => (
											<SimpleShiftTile
												ref={mergeRefs(triggerRef, focusRefs[period.id])}
												onClick={() => {
													if (activePopup === period.id) {
														setActivePopup(undefined);
													} else {
														tab(period.id);
														setActivePopup(period.id);
													}
												}}
												period={period}
												outerTimeFrame={week}
												isSelected={activePopup === period.id}
												isTabbable={tabbed?.id === period.id}
												size="small"
												hasOverrideBorder={
													period.type === PeriodType.Override ||
													period.type === PeriodType.Forwarding
												}
											/>
										)}
									/>
								))}
							</Rotation>
						))}
					/>
				);
			})}
		</div>
	);
};

export { WeekList };
